import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';
import { SingleSelect } from '@/modules/common';

import { OnboardingConflictRow } from './types';

const I18N_COMMON_NAMESPACE = 'authenticated.onboarding.common';

interface CreateOnboardingConflictColumnsParams {
  /** Labels for each person column (e.g. "Person 1", "Person 2") */
  personLabels: string[];
  /** React Hook Form instance */
  form: UseFormReturn<FieldValues>;
  /** Translation function */
  t: TFunction;
  /** Pre-translated label for the "Resulting Entity" column (e.g. "Resulting User", "Resulting Client") */
  resultingEntityLabel: string;
}

/**
 * Creates TanStack Table column definitions for the onboarding conflicts comparison table.
 * Follows the same pattern as createConflictColumns from the projects module.
 */
export const createOnboardingConflictColumns = ({
  personLabels,
  form,
  t,
  resultingEntityLabel,
}: CreateOnboardingConflictColumnsParams): ColumnDef<OnboardingConflictRow>[] => [
  // Column 1: Attribute name
  {
    id: 'attribute',
    header: () => (
      <div className="min-w-[120px]">{t(`${I18N_COMMON_NAMESPACE}:duplicates.attribute`)}</div>
    ),
    size: 140,
    cell: ({ row }) => <div className="min-w-[120px] font-medium">{row.original.fieldLabel}</div>,
  },

  // Column 2: Resulting entity (dropdown)
  {
    id: 'resultingValue',
    header: () => <div className="min-w-[200px]">{resultingEntityLabel}</div>,
    size: 200,
    cell: ({ row }) => {
      const select = (
        <SingleSelect
          name={row.original.fieldKey}
          control={form.control}
          options={row.original.selectOptions}
          placeholder={row.original.placeholder}
          searchable={false}
          disabled={row.original.selectDisabled}
          isClearSelectionEnabled={false}
        />
      );

      if (row.original.selectDisabled && row.original.selectDisabledTooltip) {
        return (
          <div className="min-w-[200px]">
            <SimpleTooltip content={row.original.selectDisabledTooltip}>
              <span className="inline-flex w-full">{select}</span>
            </SimpleTooltip>
          </div>
        );
      }

      return <div className="min-w-[200px]">{select}</div>;
    },
  },

  // Dynamic columns for each person
  ...personLabels.map(
    (label, index): ColumnDef<OnboardingConflictRow> => ({
      id: `person-${index}`,
      header: () => <div className="min-w-[140px]">{label}</div>,
      size: 160,
      cell: ({ row }) => {
        const cellData = row.original.personValues[index];
        if (!cellData) return null;

        return (
          <div
            className={cn(
              'flex min-w-[140px] items-center',
              row.original.isIntegrationRow && 'justify-center',
              cellData.isDestructive && 'text-destructive',
              cellData.isMuted && 'text-muted-foreground'
            )}
          >
            {cellData.displayValue}
          </div>
        );
      },
    })
  ),
];
